<script setup>
// FLOW-13 (SPEC Q3, S3; §6 do PLAN, itens 5 e 6; mockup aprovado, tela 5) — os nomes que ficaram nos
// agentes, abaixo do cofre e só para o super-admin. A migração dos segredos (S3) deixa no agente o
// nome que não cabe no cofre, e a lista diz qual e por quê, em quatro categorias, nesta ordem:
// - `colisao`: valores diferentes entre os agentes (a Q3). Cada um continua com o seu até a decisão,
//   e "Criar na conta" abre o "Novo segredo" com o nome, que avisa quem o guarda antes de salvar;
// - `conflito_com_o_cofre`: o cofre já tem o nome, com outro valor; pelo R2, vale o da conta na
//   próxima publicação do agente;
// - `fica_no_agente`: a chave do bot, que é de cada agente (R4);
// - `nome_fora_da_regra`: o nome fora do R6, que o cofre não cria.
//
// Os dados chegam prontos do servidor (`flowSecretsApi.ficaramNosAgentes`): o `motivo` e a `decisao`
// já vêm no singular ou no plural, e a tela os mostra como chegam. Só nomes de segredo, de agente e de
// ferramenta: nenhum valor.
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ListaDeUso from './ListaDeUso.vue';
import { ferramentasDoUso } from './usoDoSegredo';

defineProps({
  // `[{nome, categoria, agentes: [{id, nome}], motivo, decisao, usado_por}]`.
  itens: { type: Array, default: () => [] },
});

const emit = defineEmits(['criarNaConta']);

const { t } = useI18n();

// O glifo do motivo: aviso nos que esperam uma decisão; informação na chave do bot, que fica no
// agente por regra. Status é glifo colorido com texto neutro.
const GLIFO = {
  fica_no_agente: 'i-lucide-info text-n-slate-10',
};
const glifoDe = item =>
  GLIFO[item.categoria] || 'i-lucide-triangle-alert text-n-amber-11';
// Só a colisão se cria na conta: no conflito o nome já está no cofre; a chave do bot e o nome fora da
// regra não vão para ele.
const criaNaConta = item => item.categoria === 'colisao';
</script>

<template>
  <section class="flex flex-col gap-3" data-ficaram-nos-agentes>
    <header class="flex flex-col gap-1">
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h3 class="mb-0 text-heading-2 text-n-slate-12">
          {{ t('POWER_TOOLS.SECRETS.LEFT_IN_AGENTS.TITLE') }}
        </h3>
        <span
          class="inline-flex items-center gap-1.5 text-label-small text-n-slate-11"
        >
          <span
            class="i-lucide-eye-off size-3.5 text-n-slate-10"
            aria-hidden="true"
          />
          {{ t('POWER_TOOLS.SECRETS.LEFT_IN_AGENTS.ONLY_TEAM') }}
        </span>
      </div>
      <p class="mb-0 text-sm max-w-prose text-n-slate-11">
        {{ t('POWER_TOOLS.SECRETS.LEFT_IN_AGENTS.DESCRIPTION') }}
      </p>
    </header>

    <ul
      class="p-0 m-0 list-none border divide-y rounded-pz border-n-weak divide-n-weak bg-n-card shadow-pz"
    >
      <li
        v-for="item in itens"
        :key="`${item.categoria}:${item.nome}`"
        class="flex flex-col gap-2 py-3.5 pe-4 ps-5"
        data-ficou
        :data-categoria="item.categoria"
      >
        <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div class="flex flex-col flex-1 min-w-0 gap-0.5">
            <span
              class="font-mono text-sm font-medium truncate text-n-slate-12"
              data-nome-que-ficou
            >
              {{ item.nome }}
            </span>
            <span
              class="flex items-start gap-1.5 text-label-small text-n-slate-12"
              data-motivo
            >
              <span
                class="size-3.5 mt-px shrink-0"
                :class="glifoDe(item)"
                aria-hidden="true"
              />
              {{ item.motivo }}
            </span>
          </div>
          <Button
            v-if="criaNaConta(item)"
            size="xs"
            variant="faded"
            color="slate"
            data-criar-na-conta
            :label="t('POWER_TOOLS.SECRETS.LEFT_IN_AGENTS.CREATE')"
            @click="emit('criarNaConta', item.nome)"
          />
        </div>

        <div
          v-if="ferramentasDoUso(item.usado_por).length || item.decisao"
          class="flex flex-col gap-1 -ms-2.5"
        >
          <ListaDeUso :ferramentas="ferramentasDoUso(item.usado_por)" />
          <p
            v-if="item.decisao"
            class="mb-0 px-2.5 text-label-small text-n-slate-11"
            data-decisao
          >
            {{ item.decisao }}
          </p>
        </div>
      </li>
    </ul>
  </section>
</template>
